
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";

const formSchema = z.object({
  name: z.string().min(2, "İsim en az 2 karakter olmalıdır"),
  email: z.string().email("Geçerli bir email adresi giriniz"),
  message: z.string().optional(),
  phone: z.string().min(10, "Geçerli bir telefon numarası giriniz"),
  study_field: z.enum(["SAY", "EA", "SOZ", "DİL", "TYT"], {
    required_error: "Lütfen alan seçiniz",
  }),
  current_grade: z.string({
    required_error: "Lütfen sınıfınızı seçiniz",
  }),
  target_rank: z.string().optional(),
  target_department: z.string().optional(),
});

const RecommendCoach = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
      phone: "",
      current_grade: "",
      target_rank: "",
      target_department: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      // Cast the study_field value to the expected type in the database
      // This is necessary because we've added "TYT" to our form but the database expects only SAY, EA, SOZ, or DİL
      const studyField = values.study_field === "TYT" ? "SAY" : values.study_field;
      
      const { error } = await supabase
        .from('coach_recommendations')
        .insert({
          name_surname: values.name,
          email: values.email,
          phone: values.phone,
          message: values.message,
          study_field: studyField,
          current_grade: values.current_grade,
          target_rank: values.target_rank,
          target_department: values.target_department,
        });

      if (error) throw error;
      
      toast({
        title: "Başarılı!",
        description: "Bilgileriniz alındı. Size en uygun koç önerisini en kısa sürede ileteceğiz.",
      });
      
      // Wait for toast to be visible before navigating
      setTimeout(() => {
        navigate('/coaches');
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Bir hata oluştu. Lütfen tekrar deneyiniz.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background pt-16">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Size En Uygun Koçu Bulalım</h1>
          <p className="text-muted-foreground">
            Bilgilerinizi paylaşın, size en uygun koçu önerelim.
          </p>
        </div>

        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Kişisel Bilgiler</CardTitle>
            <CardDescription>
              Size en uygun koçu önerebilmemiz için lütfen bilgilerinizi paylaşın.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>İsim Soyisim</FormLabel>
                      <FormControl>
                        <Input placeholder="İsim Soyisim" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="ornek@email.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefon</FormLabel>
                      <FormControl>
                        <Input placeholder="05XX XXX XX XX" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="study_field"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Alan</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Alan seçiniz" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="SAY">Sayısal</SelectItem>
                            <SelectItem value="EA">Eşit Ağırlık</SelectItem>
                            <SelectItem value="SOZ">Sözel</SelectItem>
                            <SelectItem value="DİL">Dil</SelectItem>
                            <SelectItem value="TYT">TYT</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="current_grade"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sınıf</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Sınıf seçiniz" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="9">9. Sınıf</SelectItem>
                            <SelectItem value="10">10. Sınıf</SelectItem>
                            <SelectItem value="11">11. Sınıf</SelectItem>
                            <SelectItem value="12">12. Sınıf</SelectItem>
                            <SelectItem value="mezun">Mezun</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="target_rank"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hedef Sıralama</FormLabel>
                      <FormControl>
                        <Input placeholder="Örn: 10000" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="target_department"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hedef Bölüm</FormLabel>
                      <FormControl>
                        <Input placeholder="Boğaziçi Üniversitesi, Makine Mühendisliği" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Eklemek İstedikleriniz (Opsiyonel)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Eklemek istediğiniz notları buraya yazabilirsiniz..."
                          className="min-h-[100px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full">
                  Gönder
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RecommendCoach;
